import { jwtDecode } from "jwt-decode";

/**
 * Interface for the conversation token payload
 */
export interface ConversationTokenPayload {
  conversation_id: string;
  environment: string;
  email: string;
  anon_user_id?: string;
  ss_override?: string;
  mic_enabled?: boolean;
  user_id?: string;
  local_dev_connection_method?: string;
  // exp?: number;
  // [key: string]: any;
  ptt_hidden?: boolean;
}

/**
 * Decodes a JWT token and returns its payload
 * @param token The JWT token to decode
 * @returns The decoded token payload
 */
export function decodeJwt<T = any>(token: string): T {
  try {
    return jwtDecode<T>(token);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    throw new Error("Invalid JWT token");
  }
}

/**
 * Checks if a JWT token is expired
 * @param token The JWT token to check
 * @returns True if the token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded.exp) return true;

    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // If we can't decode the token, consider it expired
  }
}

/**
 * Gets the expiration time of a JWT token
 * @param token The JWT token
 * @returns The expiration time as a Date object, or null if not found
 */
export function getTokenExpirationTime(token: string): Date | null {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded.exp) return null;

    // exp is in seconds, convert to milliseconds for Date
    return new Date(decoded.exp * 1000);
  } catch (error) {
    console.error("Error getting token expiration time:", error);
    return null;
  }
}

/**
 * Extracts conversation information from a JWT token
 * @param token The JWT token to decode
 * @returns An object containing conversationId and environment, or null if the token is invalid or expired
 */
export function getConversationInfoFromToken(token: string): ConversationTokenPayload | null {
  try {
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.error("Token is expired");
      return null;
    }

    // Decode the token
    const decoded = decodeJwt<ConversationTokenPayload>(token);

    console.log("Decoded join token: ", decoded);

    // Check if required fields exist
    if (!decoded.conversation_id || !decoded.environment) {
      console.error("Token is missing required fields (conversationId or environment)");
      return null;
    }

    return decoded;
    
  } catch (error) {
    console.error("Error extracting conversation info from token:", error);
    return null;
  }
}
